import { Box, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import emptyPage from '../../assets/img/empty-page.png';
import { CustomSelectField } from '../../components/CustomSelectField';
import { LoadingPage } from '../../components/LoadingPage';
import { useUser } from '../../providers/userProvider';
import { customPalette } from '../../themes/customTheme';
import { apiUrl, httpClient } from '../../utils/httpUtils';
import { generateQueryParams } from '../../utils/queryParams';
import { hasRole } from '../../utils/roles';
import { MetricsCards } from '../dashboard/MetricsCards';
import { TopNotes } from '../dashboard/TopNotes';
import DatePickerValue from './DatePickerValue';
import { GraphicCard } from './GraphicCard';
import { Results } from './Results';

export const ReportList = () => {
  const t = useTranslate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [metrics, setMetrics] = useState([]);
  const [dateFrom, setDateFrom] = useState(
    dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
  );
  const [dateTo, setDateTo] = useState(
    dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
  );
  const [tempDateFrom, setTempDateFrom] = useState(
    dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
  );
  const [tempDateTo, setTempDateTo] = useState(
    dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
  );
  const [isInvalidDateRange, setIsInvalidDateRange] = useState(false);
  const { team: team_id, userId, loading: userLoading } = useUser();
  const [team, setTeam] = useState('');
  const [tempTeam, setTempTeam] = useState('');
  const [member, setMember] = useState('');
  const [tempMember, setTempMember] = useState('');

  useEffect(() => {
    if (team_id && !hasRole(['admin', 'gerente'])) {
      setTeam(team_id);
      setTempTeam(team_id);
    }
  }, [team_id]);

  useEffect(() => {
    if (userId && !hasRole(['admin', 'gerente', 'editor'])) {
      setMember(userId);
      setTempMember(userId);
    }
  }, [userId]);

  useEffect(() => {
    let isMounted = true;

    const getReports = async () => {
      try {
        setLoading(true);
        const params = {
          team_id: team,
          user_id: member,
          date_from: dateFrom,
          date_to: dateTo,
        };
        const query = generateQueryParams(params);
        const url = `${apiUrl}/metrics/reports?` + query;
        const res = await httpClient(url);
        if (res.status === 200) {
          const data = await res.json;
          const filterData = {
            visits: data.visits || 0,
            target_visits: data.target_visits,
            notes: data.notes || 0,
            target_notes: data.target_notes,
            rfv: data.rfv || 0,
            target_rfv: data.target_rfv,
            subscribers: data.subscribers || 0,
            target_subscribers: data.target_subscribers,
          };
          const allMetricsEmpty = Object.values(filterData).every(
            value => value === null || value === 0
          );

          setData(allMetricsEmpty ? {} : filterData);
          const processedMetrics = (data.metrics || []).map(item => {
            const newDate = new Date(item.view_date);
            const day = newDate.getUTCDate();
            return { ...item, date: day };
          });
          setMetrics(processedMetrics);
        }
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!userLoading) {
      if (hasRole(['admin', 'gerente'])) {
        getReports();
      } else if (hasRole(['editor']) && team) {
        getReports();
      } else if (!hasRole(['admin', 'gerente', 'editor']) && team && member) {
        getReports();
      }
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, team, member, userLoading]);

  const handleDateFromChange = event => {
    const selectedDateFrom = event?.format('YYYY-MM-DD');
    setTempDateFrom(selectedDateFrom);
    if (tempDateTo && selectedDateFrom && selectedDateFrom > tempDateTo) {
      setIsInvalidDateRange(true);
    } else {
      setIsInvalidDateRange(false);
    }
  };

  const handleDateToChange = event => {
    const selectedDateTo = event?.format('YYYY-MM-DD');
    setTempDateTo(selectedDateTo);
    if (tempDateFrom && selectedDateTo && selectedDateTo < tempDateFrom) {
      setIsInvalidDateRange(true);
    } else {
      setIsInvalidDateRange(false);
    }
  };

  useEffect(() => {
    if (hasRole(['admin', 'gerente', 'editor'])) {
      setMember('');
    }
  }, [team]);

  const handleFilter = () => {
    setDateFrom(tempDateFrom);
    setDateTo(tempDateTo);
    setTeam(tempTeam);
    setMember(tempMember);
  };

  if (loading || userLoading) return <LoadingPage />;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '20px',
        }}
      >
        <Typography
          variant="h1"
          sx={{
            textAlign: 'center',
            fontSize: '32px',
            fontWeight: '700',
          }}
        >
          {t('resources.reports.titleTeams')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            marginBottom: '10px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
            }}
          >
            <CustomSelectField
              value={tempTeam}
              disabled={
                hasRole(['redactor', 'editor']) &&
                !hasRole(['admin', 'gerente'])
              }
              setValue={setTempTeam}
              field="teams"
              label="Equipo"
            />

            <CustomSelectField
              value={tempMember}
              disabled={!hasRole(['admin', 'gerente', 'editor'])}
              filterTeam={tempTeam || null}
              setValue={setTempMember}
              field="members"
              label="Redactor"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              label: { color: customPalette.primary.main, fontWeight: '500' },
            }}
          >
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <DatePickerValue
                value={tempDateFrom}
                label="Desde"
                onChange={handleDateFromChange}
              />
              <DatePickerValue
                label="Hasta"
                value={tempDateTo}
                onChange={handleDateToChange}
              />
            </Box>

            {isInvalidDateRange ? (
              <Typography
                color="error"
                sx={{
                  lineHeight: '1.1',
                  position: 'absolute',
                  bottom: '-20px',
                  left: '0',
                  fontSize: '14px',
                }}
              >
                {t('resources.reports.list.error_message')}
              </Typography>
            ) : null}
          </Box>
          <Button variant="contained" onClick={handleFilter}>
            <span>FILTRAR</span>
          </Button>
        </Box>
      </Box>
      {Object.keys(data).length === 0 ? (
        <Box component={'section'} sx={{ mt: '20px', textAlign: 'center' }}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'center'}
            gap={4}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '80px',
            }}
          >
            <img width={400} src={emptyPage} alt="No hay objetivos" />
            <Box sx={{ maxWidth: '740px', margin: '0 auto' }}>
              <Typography
                variant="h6"
                sx={{
                  textAlign: 'center',
                  fontSize: '24px',
                  fontWeight: '600',
                }}
              >
                {t('emptyPage.title')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  maxWidth: '500px',
                  margin: '0 auto',
                  textAlign: 'center',
                  fontSize: '16px',
                }}
              >
                {t('emptyPage.subtitle')}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box>
          {data && Object.keys(data).length > 0 ? (
            <MetricsCards metrics={data} progressBar={false} />
          ) : null}
          {metrics?.length > 0 && metrics ? (
            <GraphicCard data={metrics} dateFrom={dateFrom} dateTo={dateTo} />
          ) : null}

          {hasRole(['admin', 'gerente', 'editor']) && dateFrom && dateTo ? (
            <Results
              teamId={hasRole(['admin', 'gerente']) ? null : team}
              userId={member}
              dateTo={dateTo}
              dateFrom={dateFrom}
            />
          ) : null}

          <TopNotes
            title={t('resources.reports.list.title_top_notes')}
            teamId={hasRole(['admin', 'gerente']) ? team : team_id}
            userId={member}
            dateTo={dateTo}
            dateFrom={dateFrom}
          />
        </Box>
      )}
    </>
  );
};
